import cx from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';

import typography from '~styles/typography.scss';
import { PageContent } from 'restaurantAdmin/layout/PageContent';
import { PageHeader } from 'restaurantAdmin/layout/PageHeader';
import { Icon } from '@components/icon/Icon';
import { useMessagingDrawer, useRestaurantMessagesQuery } from './hooks';
import styles from './InboxPage.scss';
import { InboxConversationRow } from './InboxConversationRow';
import { MessageSender } from './apiHelpers';
import { Link } from 'react-router-dom';
import {
  buildConversationSummaries,
  groupInboxConversationsByDay,
  formatInboxConversationRelativeTime,
} from './inboxConversationSummaries';

const CONVERSATION_PAGE_SIZE = 40;

const inboxThreadMessageSenders = [
  MessageSender.Guest,
  MessageSender.Restaurant,
];

function drawerRecipientIdSet(
  guestIds: string[],
  walkInIds: string[],
): Set<string> {
  const raw = [...guestIds, ...walkInIds];
  return new Set(
    raw.flatMap((id) =>
      id
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  );
}

export const InboxPage = () => {
  const { messagingDrawerSearchParams, isOpen, guestIds, walkInIds } =
    useMessagingDrawer();

  const selectedRecipientIds = useMemo(
    () => drawerRecipientIdSet(guestIds, walkInIds),
    [guestIds, walkInIds],
  );

  const isConversationSelected = (userId: string) =>
    isOpen && selectedRecipientIds.has(userId);

  const { data: notifications = [] } = useRestaurantMessagesQuery({
    // Conversation list is derived from this page of messages; guests whose
    // latest message falls outside this window may be missing from the inbox.
    limit: 500,
    unreadOnly: false,
    sender: inboxThreadMessageSenders,
  });

  const conversations = useMemo(
    () => buildConversationSummaries(notifications),
    [notifications],
  );

  const [visibleCount, setVisibleCount] = useState(CONVERSATION_PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setVisibleCount(Math.min(CONVERSATION_PAGE_SIZE, conversations.length));
  }, [conversations]);

  useEffect(() => {
    const infiniteScrollSentinel = sentinelRef.current;
    if (!infiniteScrollSentinel || visibleCount >= conversations.length) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((prev) =>
            Math.min(prev + CONVERSATION_PAGE_SIZE, conversations.length),
          );
        }
      },
      { rootMargin: '120px' },
    );

    observer.observe(infiniteScrollSentinel);
    return () => observer.disconnect();
  }, [conversations.length, visibleCount]);

  const visibleConversations = conversations.slice(0, visibleCount);
  const conversationDayGroups = useMemo(
    () => groupInboxConversationsByDay(visibleConversations),
    [visibleConversations],
  );

  return (
    <>
      <PageHeader title="Inbox"></PageHeader>

      <PageContent>
        <div className={styles.layout}>
          {notifications.length ? (
            <div className={styles.notificationContainer}>
              {conversationDayGroups.map((dayGroup) => (
                <section
                  key={dayGroup.dayKey}
                  className={styles.dateGroup}
                  aria-labelledby={`inbox-day-${dayGroup.dayKey}`}
                >
                  <h2
                    className={cx(typography.h5, styles.dateGroupTitle)}
                    id={`inbox-day-${dayGroup.dayKey}`}
                  >
                    {dayGroup.sectionLabel}
                  </h2>
                  <ul className={styles.notificationList}>
                    {dayGroup.conversations.map((notification) => {
                      const selected = isConversationSelected(
                        notification.user.id,
                      );
                      return (
                        <li
                          key={notification.user.id}
                          className={cx(styles.notification, {
                            [styles.selected]: selected,
                          })}
                        >
                          <Link
                            className={styles.notificationLink}
                            to={
                              '?' +
                              messagingDrawerSearchParams([
                                notification.user.id,
                              ])
                            }
                            aria-label={`Open messaging drawer for ${notification.user.firstName} ${notification.user.lastName}`}
                            aria-current={selected ? 'true' : undefined}
                          >
                            <InboxConversationRow
                              notification={notification}
                              timestampLabel={formatInboxConversationRelativeTime(
                                notification.createdAt,
                              )}
                              isSelected={selected}
                            />
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
              {visibleCount < conversations.length ? (
                <div
                  ref={sentinelRef}
                  className={styles.infiniteScrollSentinel}
                  aria-hidden
                />
              ) : null}
            </div>
          ) : (
            <div className={styles.emptyStateContainer}>
              <Icon name="inbox" className={styles.inboxIcon} />
              <h2 className={cx(typography.h4)}>No items</h2>
            </div>
          )}
        </div>
      </PageContent>
    </>
  );
};
